import React, { useState, useEffect, useContext, useCallback } from "react";
import { AppSettings } from "../../config/app-settings.js";
import Preloader from "../../components/custom/preLoader.js";
import axiosInstance, {
  getUserDetails,
  isCompanyManagedBillingUser,
  mergeUserDetails,
} from "../../components/content/axiosConfig";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useHistory, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import showErrorToast, { showSubscriptionSuccessToast } from "../../components/toast/toast";
import StripePaymentSection from "../../components/custom/StripePaymentSection.jsx";
import { checkoutPageStyles } from "../../components/custom/checkoutPageStyles.js";

const ExtraPondPayment = () => {
  const history = useHistory();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const context = useContext(AppSettings);
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const returnPath = new URLSearchParams(location.search).get("return") || "/createPond";

  const fetchPaymentData = useCallback(async () => {
    setLoading(true);
    try {
      const subRes = await axiosInstance.get("api/fisher/SubscriptionInfo");
      setSubscriptionInfo(subRes.data);
      if (subRes.data?.ManagedByCompanyID || subRes.data?.isCompanyManagedBilling) {
        mergeUserDetails({
          ManagedByCompanyID: subRes.data.ManagedByCompanyID ?? null,
          isCompanyManagedBilling: true,
        });
        const managedMsg =
          "Billing for this account is managed by your Pond Management Company. Contact your company manager to purchase an extra pond slot.";
        setError(managedMsg);
        showErrorToast(managedMsg);
        return;
      }
      if (isCompanyManagedBillingUser()) {
        const managedMsg =
          "Billing for this account is managed by your Pond Management Company. Contact your company manager to purchase an extra pond slot.";
        setError(managedMsg);
        showErrorToast(managedMsg);
        return;
      }

      const paymentIntentResponse = await axiosInstance.post("api/create-payment-intent", {
        type: "extra_pond",
      });

      const paymentIntentData = paymentIntentResponse.data;
      if (!paymentIntentData.clientSecret) {
        throw new Error("Failed to retrieve clientSecret from server.");
      }
      setClientSecret(paymentIntentData.clientSecret);
      setAmount(paymentIntentData.amount);
    } catch (err) {
      const data = err.response?.data;
      const errorMessage =
        data?.error || data?.message || err.message || "Failed to load payment details";
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(false);
    context.handleSetAppContentClass("p-0");
    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass("");
    };
  }, [context]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      showErrorToast("Payment system is not ready. Please try again.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      showErrorToast("Payment form is not loaded. Please try again.");
      return;
    }

    setLoading(true);
    try {
      const user = getUserDetails();
      const billingName = user.FullName || "PondStat Customer";
      const billingEmail = user.Email || "";

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: billingName,
            email: billingEmail || undefined,
          },
        },
        receipt_email: billingEmail || undefined,
      });

      if (stripeError) {
        setError(stripeError.message);
        showErrorToast(`Payment failed: ${stripeError.message}`);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        const purchaseResponse = await axiosInstance.post("api/fisher/PurchaseExtraPondSlot", {
          StripePaymentIntentId: paymentIntent.id,
        });
        const notification =
          purchaseResponse.data?.notification ||
          purchaseResponse.data?.message ||
          "Extra pond slot purchased successfully.";
        showSubscriptionSuccessToast(notification);
        history.push(returnPath);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Payment failed";
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!stripe || !elements || (loading && !clientSecret && !error)) {
    return <Preloader />;
  }

  const companyManaged = !!(
    error &&
    (subscriptionInfo?.ManagedByCompanyID ||
      subscriptionInfo?.isCompanyManagedBilling ||
      isCompanyManagedBillingUser() ||
      /Pond Management Company/i.test(error))
  );

  return (
    <form onSubmit={handleSubmit}>
      <style>{checkoutPageStyles}</style>
      <div className="pondstat-checkout-page">
        <div className="pondstat-checkout-page__container">
          <button
            type="button"
            className="pondstat-checkout-page__back"
            onClick={() => history.push(returnPath)}
          >
            <Icon icon="solar:arrow-left-linear" width={18} />
            Go back
          </button>

          <div className="pondstat-checkout-summary">
            <div className="pondstat-checkout-summary__body">
              <span className="pondstat-checkout-summary__badge">Add-on</span>
              <div className="h5 fw-bold mb-2">Extra pond slot</div>
              {companyManaged ? (
                <p className="text-muted small mb-0">{error}</p>
              ) : (
                <>
                  <p className="text-muted small mb-3">
                    Your {subscriptionInfo?.package?.Name || "Multi-Pond"} plan includes{" "}
                    {subscriptionInfo?.LicensedPondCount ?? 1} licensed pond
                    {(subscriptionInfo?.LicensedPondCount ?? 1) === 1 ? "" : "s"}.
                    Add one more slot to create another pond.
                  </p>
                  <div className="display-6 fw-bold mb-0 text-dark">
                    ${Number(amount || 0).toFixed(2)}
                    <small className="h6 text-body text-opacity-50 ms-1">/ year</small>
                  </div>
                </>
              )}
            </div>
          </div>

          {companyManaged ? (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => history.push(returnPath)}
              >
                Go back
              </button>
            </div>
          ) : (
            <StripePaymentSection
              amount={amount}
              loading={loading}
              disabled={!clientSecret}
              error={error}
              onCancel={() => history.push(returnPath)}
              cancelLabel="Cancel"
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default ExtraPondPayment;
